import moment from "moment";
import { DateReferenceOption } from "stores/ComponentStores/DatePickerComponentStore";
import BaseService from "./BaseService";
import { toIsoByDateReference } from "utils/DateTimeUtils";

/** Estimated total tokens for one underlying model across an LLM reclassify. */
export interface LlmReclassifyTokenEstimate {
    modelName: string;
    inputTokens: number;
    cachedInputTokens: number;
    outputTokens: number;
}

interface CheckPayload {
    numRecords: number;
    // LLM only (CheckReclassificationV2)
    tokenEstimate?: LlmReclassifyTokenEstimate[];
    classifiersWithoutUsage?: string[];
}

interface ReclassifyResult {
    succeeded: number;
    failed: number;
}

export type ReclassifyJobStatus =
    | "Queued"
    | "Running"
    | "Completed"
    | "Failed"
    | "Timeout";

export interface ReclassifyJobResult {
    id: string;
    startDate: string;
    endDate: string;
    dateReference: string;
    userName: string;
    hierarchies: string[];
    classifiers: string[];
    status: ReclassifyJobStatus;
    failureReason?: string;
    executionStartDateTimeUtc: string;
    executionEndDateTimeUtc: string;
    totalRecords: number;
    processedRecords: number;
}

export type ReclassifyJobStatusV2 =
    | "Queued"
    | "Scheduling"
    | "Scheduled"
    | "Processing"
    | "Completed"
    | "Failed";

export interface ReclassifyJobResultV2 {
    id: string;
    status: ReclassifyJobStatusV2;
    failureReason?: string;
    startDate: string;
    endDate: string;
    dateReference: string;
    createdOn: string;
    startedAt?: string;
    completedAt?: string;
    totalRecords: number;
    processedRecords: number;
    allHierarchies: boolean;
    userName?: string;
    hierarchies?: string[];
    classifiers?: string[];
    classifierIds: string[];
    // LLM only: estimated from the models' reclassification usage on the days the job ran
    tokenUsage: LlmReclassifyTokenEstimate[];
}

export class ClassificationService extends BaseService {
    protected urlBase = "api/Classification";

    public async CheckReclassification(
        orgId: string,
        startDate: moment.Moment,
        endDate: moment.Moment,
        dateReference: DateReferenceOption,
        hierarchyIds?: string[],
        classifierIds?: string[],
        useReclassifyV2: boolean = false,
    ) {
        const { beginDate: start, endDate: end } = toIsoByDateReference(
            startDate,
            endDate,
            dateReference,
        );

        // const params = new URLSearchParams({
        //     orgId: orgId || "",
        //     startDate: startDate?.format() || "",
        //     endDate: endDate?.format() || "",
        //     dateReference: dateReference.valueOf(),
        //     hierarchyIds: hierarchyIds?.join(",") || "",
        //     classifierIds: classifierIds?.join(",") || "",
        // });
        const params = new URLSearchParams();
        let rv: CheckPayload = { numRecords: 0 };
        const body = {
            orgId: orgId || "",
            startDate: start!,
            endDate: end!,
            dateReference: dateReference.valueOf(),
            classifierIds: classifierIds || [],
            hierarchyIds: hierarchyIds || [],
        };
        if (!useReclassifyV2) {
            rv = await this.post<CheckPayload>(
                JSON.stringify(body),
                params,
                "CheckReclassification",
            );
        } else {
            rv = await this.post<CheckPayload>(
                JSON.stringify(body),
                params,
                "CheckReclassificationV2",
            );
        }
        return rv;
    }

    public async Reclassify(
        orgId: string,
        sessionId: string,
        startDate: moment.Moment,
        endDate: moment.Moment,
        dateReference: DateReferenceOption,
        hierarchyIds?: string[],
        classifierIds?: string[],
        useReclassifyV2: boolean = true,
        totalRecordCount: number = 0,
    ) {
        const { beginDate: start, endDate: end } = toIsoByDateReference(
            startDate,
            endDate,
            dateReference,
        );

        const params = new URLSearchParams();
        if (!useReclassifyV2) {
            const body = {
                orgId: orgId || "",
                sessionId: sessionId || "",
                startDate: start!,
                endDate: end!,
                dateReference: dateReference.valueOf(),
                classifierIds: classifierIds || [],
                hierarchyIds: hierarchyIds || [],
            };
    
            await this.post<ReclassifyResult>(
                JSON.stringify(body),
                params,
                "Reclassify",
            );   
        } else {
            const body = {
                startDate: start!,
                endDate: end!,
                dateReference: dateReference.valueOf(),
                classifierIds: classifierIds || [],
                hierarchyIds: hierarchyIds || [],
                allHierarchies: true, // needs implemented
                totalRecordCount: totalRecordCount
            };

            await this.post<ReclassifyResult>(
                JSON.stringify(body),
                params,
                "ReclassifyV2"
            )
        }
        return;
    }

    public async reclassifyResults(): Promise<ReclassifyJobResult[]> {
        return this.get(new URLSearchParams(), "ReclassifyResults");
    }

    public async reclassifyResultsV2(
        classifierType: "Lucene" | "LLM",
    ): Promise<ReclassifyJobResultV2[]> {
        return this.get(
            new URLSearchParams({ classifierType }),
            "ReclassifyResultsV2",
        );
    }
}
